import { mark, measure, onDOMReady } from '@immediate_media/script-utils';
import { waitForConsent } from '@immediate_media/consent-checker';
import { trackPerformance, isTimeToCmpMarkerSet } from './lib/gpt/performance.js';

import { setupAdRefresh, setupScheduledAdRefresh } from './lib/refresh/setup.js';

import { addSpeedCurveLuxData } from './lib/lux.js';
import { logger } from './lib/logger.js';
import { getGlobalSettings } from './lib/settings.js';
import { requestBids } from './lib/request.js';
import { defineAdSlots } from './lib/define.js';
import { watchForDynamicAdSlots } from './lib/watch.js';
import { setupPermutiveTargeting } from './lib/targeting/permutive.js';
import { setGlobal } from './lib/global.js';
import { debugMessage, isAdSlotsDebugModeOn } from './lib/debug.js';
import { setMantisData } from './lib/mantis.js';

import {
  getRecipeDebugOverlay,
  getLongFormDebugOverlay,
} from './lib/stitch-debug/index.js';
import { setupGptLazyLoading, enableGptServices } from './lib/gpt/utils.js';
import { api } from './lib/api/api.js';
import { onReady, ready } from './lib/api/ready.js';
import {
  getDynamicAdsPlaceholders,
  loadDynamicAds,
  getDynamicAdsConfig,
} from './lib/dynamic-ad-injection/define-ad-slots.js';
import { isMobile, isDynamicAdsEnabled } from './lib/dom.js';
import adConfig from './ad-config-example.json';
import { identityGUIDS } from './lib/identity-guids/index.js';
/**
 * A script to run our ad management code.
 *
 * This is bundled for use as a separate async script for improved ad speed.
 *
 * Please keep this script clean, avoid any external dependencies, and make
 * sure any new functionality is fully tested. If you're thinking of adding
 * something here consider if it really needs to live here, or is there a more
 * suitable home for it. For example, we had a bunch of prop mapping stuff for
 * our React components that may as well live with the React components, being
 * the only place they're actually used, rather than complicate this script. We
 * want to keep it minimal and fast, as far as possible! 💰💰💰
 *
 * See the README of the @immediate_media/advertising package for usage details.
 */
window.googletag = window.googletag || { cmd: [] };

mark('time-to-ad-manager-loaded');

debugMessage('Ad manager loaded');

// Part of dynamic ad injection logic
let placeholders = [];

const displayAds = (globalSettings) => {
  setMantisData(globalSettings?.mantis);
  setupGptLazyLoading(globalSettings);
  setupPermutiveTargeting();

  // setupLazyLoading includes a call to collapseEmptyDivs, which must
  // come before enableServices
  enableGptServices();

  const slots = defineAdSlots();

  // keep above requests
  trackPerformance();
  addSpeedCurveLuxData(globalSettings);

  requestBids(slots);

  setupAdRefresh();
  setupScheduledAdRefresh();

  const type = globalSettings.targets?.channel;

  // Dynamic ad injection logic turned off temporary until
  // switch to turn it on/off is created in WP
  if (isDynamicAdsEnabled(globalSettings.stitcher) && isMobile()) {
    loadDynamicAds(placeholders, adConfig, globalSettings);
  }

  watchForDynamicAdSlots();

  // check for the debug param present in the url
  if (isAdSlotsDebugModeOn(window.location.search)) {
    // Later add the logic realted to debug mode
    debugMessage('Debug mode enabled');

    if (!type) {
      logger.error('Stitch debug could not be loaded');
    }

    // The default settings are switched to use ad-config-example.json file
    if (type === 'recipes') {
      getRecipeDebugOverlay({
        charLimits: adConfig.characterLimit.recipes,
      });
    } else if (type && type !== 'recipes') {
      getLongFormDebugOverlay({
        charLimits: adConfig.characterLimit.longForm,
        adsNumber: getDynamicAdsConfig(adConfig, globalSettings).maxAds,
      });
    }
  }
};

/**
 * Init ads.
 */
const init = () => {
  // we need to track whether this function has already been called
  // as changes to the consent string (managed by __tcfapi), will
  // cause it to be called multiple times, resulting in unexpected
  // behaviour
  const alreadyInit = window?.IM?.advertising?.init;

  if (alreadyInit) {
    debugMessage('Ad package is already initialised, exiting...');
    return;
  }

  setGlobal('init', true);

  mark('time-to-ad-manager-started');

  googletag.cmd.push(() => {
    mark('time-to-gpt-started');

    if (isTimeToCmpMarkerSet()) {
      measure('cmp-to-gpt-started', 'time-to-cmp', 'time-to-gpt-started');
    }

    // Make any calls that don't rely on markup being present as soon as possible
    googletag.pubads().disableInitialLoad();
    googletag.pubads().enableSingleRequest();

    // Display the ads when the DOM has fully loaded. This means that the markup
    // for all ad slots will be present and we can guarantee roadblocks.
    // See https://support.google.com/admanager/answer/183282
    onDOMReady(() => {
      let globalSettings = getGlobalSettings();
      const settingsAttempts = 10;
      const settingsAttemptTimeout = 200;

      (function loadAdsWithSettings(remainingAttempts) {
        if (globalSettings) {
          displayAds(globalSettings);
          return;
        }

        setTimeout(() => {
          // eslint-disable-next-line no-param-reassign
          remainingAttempts -= 1;
          if (remainingAttempts >= 0) {
            globalSettings = getGlobalSettings();
            loadAdsWithSettings(remainingAttempts);
          } else {
            logger.error(
              `Ads could not get globalSettings, globalSettings not retrieved after ${settingsAttempts} attempts`,
            );
          }
        }, settingsAttemptTimeout);
      }(settingsAttempts));
      identityGUIDS();
    });
  });
};

// Make sure api.ready() is called once dom is loaded and all potential
// snippets are parsed and it hasn't been called in any of them.
onDOMReady(ready(api));

// Run once api.ready() is called and queue is processed,
// possibly before DOMContentLoaded event and before consent is given
onReady(() => {
  const globalSettings = getGlobalSettings();
  placeholders = getDynamicAdsPlaceholders(
    globalSettings,
    getDynamicAdsConfig(adConfig, globalSettings),
  );
});

// Run once consent is given
waitForConsent(init);
